import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { H4, Small } from "@/components/ui/typography"
import {
	AddressAutocomplete,
	GoogleAddressAutocomplete,
} from "@/components/primitives/AddressAutocomplete"
import React from "react"
import { FormField, FormItem } from "../ui/form"
import { useForm, useWatch } from "react-hook-form"
import { FormLabel } from "@chakra-ui/react"
import { Map, Marker } from "@vis.gl/react-google-maps"
import { RHFProvider } from "./RHFProvider"
import { AddressType } from "@/state/draft/hqAddress"
import { getLatLng } from "use-places-autocomplete"
import { useAddressAutocomplete } from "@/hooks/useAddressAutocomplete"
import { milesToMeters } from "@/state/reportv2/convert"
import { GoogleMapCircle } from "../primitives/GoogleMapCircle"
import { useOSM } from "@/features/map/useOSM"
import { fromLatLon } from "@/features/map/fromLatLon"
import { OSMapComponent } from "@/features/map/OSMapComponent"

type RestoreAddressProps = {
	onRestore: (params: RestoreAddressForm) => void
	defaultValues: RestoreAddressForm
	restoringInProgress: boolean
}

export type RestoreAddressForm = {
	count: number
	address: AddressType
	radiusMiles: number
}

export const RestoreAddress: React.FC<RestoreAddressProps> = ({
	onRestore,
	defaultValues,
	restoringInProgress,
}) => {
	const form = useForm<RestoreAddressForm>({
		defaultValues: defaultValues,
	})

	const formValues = useWatch({
		control: form.control,
		defaultValue: defaultValues,
	}) as RestoreAddressForm

	const hqAddr = useOSM({
		initialView: {
			center: fromLatLon(formValues.address.latlon),
			zoom: 15,
		},
		onMarkerDragEnd: (e) => {},
		defaultAddress: defaultValues.address?.address,
		defaultLatLon: defaultValues.address
			? {
					lat: defaultValues.address.latlon[0],
					lon: defaultValues.address.latlon[1],
			  }
			: undefined,
		onSelectedPlaceChange: (place) => {
			const addr: AddressType = {
				address: place.displayName,
				latlon: [place.lat, place.lon],
			}
			form.setValue("address", addr)
		},
	})

	const radiusMeters = milesToMeters(form.getValues("radiusMiles"))
	const handleSubmit = (data: RestoreAddressForm) => {
		onRestore(data)
	}

	return (
		<RHFProvider form={form} onSubmit={form.handleSubmit(handleSubmit)}>
			<div className="flex flex-col gap-8">
				<section>
					<H4>Restore with AI</H4>
					<Small className="text-muted-foreground mt-1.5">
						MileKeeper AI will help you find business addresses at the
						selected location and add them to your report
					</Small>
				</section>
				<section className="flex flex-col gap-3.5">
					<FormField
						control={form.control}
						name="count"
						render={({ field }) => {
							return (
								<FormItem>
									<FormLabel>How many addresses need to add?</FormLabel>
									<Input
										type="number"
										value={field.value}
										onChange={(e) =>
											field.onChange(Number(e.target.value))
										}
									/>
									<Slider
										className="mt-3.5"
										value={[field.value]}
										onValueChange={([value]) =>
											field.onChange(value!)
										}
										max={100}
										step={1}
									/>
								</FormItem>
							)
						}}
					/>
					<FormField
						control={form.control}
						name="address"
						render={({ field }) => (
							<FormItem className="space-y-2">
								<FormLabel>Where to find the address</FormLabel>
								<AddressAutocomplete
									suggestions={hqAddr.autocomplete.suggestions}
									onSuggestionSelect={hqAddr.selectPlace}
									ref={hqAddr.autocomplete.сontainerRef}
								>
									<Input
										autoComplete="off"
										placeholder="Enter address"
										value={hqAddr.autocomplete.input.value}
										onChange={(e) => {
											hqAddr.autocomplete.input.onChange(
												e.target.value
											)
										}}
									/>
								</AddressAutocomplete>
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="radiusMiles"
						render={({ field }) => (
							<FormItem className="space-y-2">
								<FormLabel>Radius for searching address</FormLabel>
								<Select
									value={field.value.toString()}
									onValueChange={(v) => field.onChange(Number(v))}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select radius" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="3">3 miles</SelectItem>
										<SelectItem value="5">5 miles</SelectItem>
										<SelectItem value="7">7 miles</SelectItem>
										<SelectItem value="10">10 miles</SelectItem>
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>
					<div className="h-[220px] relative">
						<OSMapComponent
							view={[hqAddr.view, hqAddr.setView]}
							marker={hqAddr.marker}
							initial={hqAddr.initial}
							circleRadiusMeters={radiusMeters}
						/>
					</div>
				</section>
				<Button disabled={restoringInProgress} className="self-end" type="submit">
					{restoringInProgress
						? "Restoring..."
						: `Restore ${form.getValues().count} Addresses`}
				</Button>
			</div>
		</RHFProvider>
	)
}
